import axios from 'axios';
import { tokenStorage as defaultTokenStorage } from '@core/shared-utils/browser';

// Jury-Endpunkte, die ohne Access-Token erreichbar sein muessen.
const JURY_PUBLIC_PATHS = ['/jury/requestOtp', '/jury/auth', '/jury/refresh'];

const isFlagEnabled = (value) => value === true || value === 'true';

/**
 * Baut eine axios-Instanz mit der gemeinsamen Interceptor-Kette der beiden
 * Kunden-Frontends (Archiv-Modus, AWS-WAF, Jury-JWT inkl. Silent-Refresh).
 *
 * Alle Produkt- und Umgebungsunterschiede kommen ueber `process.env.REACT_APP_*`
 * (Default) oder explizit ueber die Options herein — es gibt bewusst keinen
 * Produkt-Konditional in diesem Modul.
 *
 * @param {object} [options]
 * @param {string} [options.baseURL]                 Default: `process.env.REACT_APP_API_URL`
 * @param {boolean} [options.archival]               Default: `REACT_APP_BUILD_FOR_ARCHIVAL === 'true'`
 * @param {boolean} [options.wafIntegration]         Default: `REACT_APP_AWS_WAF_INTEGRATION_USE === 'true'`
 * @param {object} [options.tokenStorage]            Default: `tokenStorage` aus `@core/shared-utils/browser`
 * @param {Function} [options.wafTokenProvider]      Default: `window.AwsWafIntegration.getToken`
 * @param {string} [options.loginRedirectPath]       Default: `/jury/login`
 * @returns {import('axios').AxiosInstance}
 */
export const createHttpClient = (options = {}) => {
    const {
        baseURL = process.env.REACT_APP_API_URL,
        archival = isFlagEnabled(process.env.REACT_APP_BUILD_FOR_ARCHIVAL),
        wafIntegration = isFlagEnabled(process.env.REACT_APP_AWS_WAF_INTEGRATION_USE),
        tokenStorage = defaultTokenStorage,
        wafTokenProvider = () => window.AwsWafIntegration.getToken(),
        loginRedirectPath = '/jury/login',
    } = options;

    const instance = axios.create({ baseURL });

    if (archival) {
        // Caches fuer die Snapshots; pro Instanz, damit mehrere Clients sich nicht teilen.
        let projects = null;
        let ctas = null;

        // Function to fetch the projects.json file
        const fetchProjectsJson = async () => {
            if (!projects) {
                try {
                    const response = await axios.get('/api/v1/projects.json');
                    projects = response.data;
                } catch (error) {
                    console.error('Error fetching projects.json:', error);
                }
            }
        };

        const fetchCtas = async () => {
            if (!ctas) {
                try {
                    const response = await axios.get('/api/v1/cta.json');
                    ctas = response.data;
                } catch (error) {
                    console.error('Error fetching cta.json:', error);
                }
            }
        };

        // Add a request interceptor
        instance.interceptors.request.use(
            async (config) => {
                let url = config.url;

                // these load composites of multiple subroutes
                if (url.includes('/projects')) await fetchProjectsJson(); // Ensure projects.json is fetched
                if (url.includes('/cta')) await fetchCtas();

                // This is necessary to ensure that the archived frontend does not accidentally call to a different origin.
                // In this way, it will always to its current origin.
                config.baseURL = window.location.origin + '/api/v1';

                // In the case of campaigns without categories, the api is still called with the query, causing the lookup to fail
                // even though the appropriate response is in there. This code fixes that URL accordingly, leading to the
                // next if-condition to match
                if (url.includes('/projects?') && !projects[url]) {
                    url = url.replace('/projects?', '/projects/?');
                }
                if (url === '/projects/?category=' && !projects[url]) {
                    url = '/projects/?category=undefined';
                }
                if (url === '/projects/?category=undefined' && !projects[url]) {
                    url = '/projects';
                }

                // Check if the URL matches any entry in the projects.json file
                if (projects && projects[url]) {
                    // If a match is found, return the data from the projects.json file
                    return {
                        ...config,
                        adapter: () => {
                            return new Promise((resolve) => {
                                resolve({
                                    data: projects[url],
                                    status: 200,
                                    statusText: 'OK',
                                    headers: {},
                                    config,
                                    request: {},
                                });
                            });
                        },
                    };
                }
                // Check if the cta-composite was loaded (which only happens when running in archival-mode)
                if (ctas && url.includes('/cta')) {
                    // since the composite contains all ctas for all pages, we need to make sure only the ones for the page
                    // requested will be returned to the code.
                    const searchParams = new URLSearchParams(url);
                    const page = searchParams.get('page') ?? 'home'; // this is the server default, so '/cta/' will return 'home' page ctas
                    const ctasForPage = ctas.filter((cta) => cta.page === page);

                    if (page) {
                        return {
                            ...config,
                            adapter: () => {
                                return new Promise((resolve) => {
                                    resolve({
                                        data: ctasForPage,
                                        status: 200,
                                        statusText: 'OK',
                                        headers: {},
                                        config,
                                        request: {},
                                    });
                                });
                            },
                        };
                    }
                }

                // Since there is no backend anymore, sending the analytics-state is unsupported
                // it can simply be answered as successful.
                if (['post', 'POST'].includes(config.method) && url.includes('/analytics')) {
                    return {
                        ...config,
                        adapter: () => {
                            return new Promise((resolve) => {
                                resolve({
                                    status: 201,
                                    statusText: 'OK',
                                    headers: {},
                                    config,
                                    request: {},
                                });
                            });
                        },
                    };
                }

                // If BUILD_FOR_ARCHIVAL is true, append .json to non-project URLs
                if (!url.startsWith('/projects/')) {
                    // Remove trailing slash if present
                    if (url.endsWith('/')) {
                        config.url = url.slice(0, -1);
                    }
                    config.url = `${config.url}.json`;
                }
                // If no match is found, proceed with the actual request
                return config;
            },
            (error) => {
                return Promise.reject(error);
            }
        );
    }

    // When using WAF, handle waf-protected calls with
    if (wafIntegration) {
        // Request interceptor to handle protected routes
        instance.interceptors.request.use(
            async (config) => {
                // if the request is marked to be for a waf-protected endpoint, then configure the request accordingly.
                if (config.wafProtected === true) {
                    try {
                        const token = await wafTokenProvider();

                        config.headers['x-aws-waf-token'] = token;
                    } catch (error) {
                        console.error('Failed to get AWS WAF token or fetch request failed', error);
                        throw error;
                    }
                }

                return config;
            },
            (error) => {
                return Promise.reject(error);
            }
        );
    }

    // Jury JWT interceptors

    // attach the in-memory access token to every /jury/* request
    // (except the public auth endpoints that don't need it).
    instance.interceptors.request.use(
        (config) => {
            const url = config.url || '';
            const isJuryRoute = url.startsWith('/jury/');
            const isPublic = JURY_PUBLIC_PATHS.some((p) => url.startsWith(p));

            if (isJuryRoute) {
                config.withCredentials = true;

                if (!isPublic) {
                    const token = tokenStorage.getAccessToken();
                    if (token) {
                        config.headers = config.headers ?? {};
                        config.headers['Authorization'] = `Bearer ${token}`;
                    }
                }
            }

            return config;
        },
        (error) => Promise.reject(error)
    );

    // On 401/403 for jury routes, attempt a silent token refresh via the
    // httpOnly-cookie-backed /jury/refresh endpoint.
    // If that succeeds the original request is retried once with the new token.
    // If refresh also fails the user is redirected to the login route.
    let isRefreshing = false;
    let refreshSubscribers = [];

    const onRefreshed = (newToken) => {
        refreshSubscribers.forEach((cb) => cb(newToken));
        refreshSubscribers = [];
    };

    const addRefreshSubscriber = (cb) => {
        refreshSubscribers.push(cb);
    };

    instance.interceptors.response.use(
        (response) => response,
        async (error) => {
            const originalRequest = error.config;
            const status = error.response?.status;
            const url = originalRequest?.url || '';

            // Only intercept jury protected routes. Skip public paths and the
            // refresh endpoint itself to prevent infinite loops.
            const isJuryProtected =
                url.startsWith('/jury/') &&
                !JURY_PUBLIC_PATHS.some((p) => url.startsWith(p)) &&
                !originalRequest._retried;

            if ((status === 401 || status === 403) && isJuryProtected) {
                originalRequest._retried = true;

                if (isRefreshing) {
                    // Queue this request until the refresh completes.
                    return new Promise((resolve) => {
                        addRefreshSubscriber((newToken) => {
                            originalRequest.headers = originalRequest.headers ?? {};
                            originalRequest.headers['Authorization'] = `Bearer ${newToken}`;
                            resolve(instance(originalRequest));
                        });
                    });
                }

                isRefreshing = true;

                try {
                    const refreshRes = await instance.get('/jury/refresh');
                    const newToken = refreshRes.data.accessToken;
                    tokenStorage.setAccessToken(newToken);
                    isRefreshing = false;
                    onRefreshed(newToken);

                    // Retry the original request with the fresh token.
                    originalRequest.headers = originalRequest.headers ?? {};
                    originalRequest.headers['Authorization'] = `Bearer ${newToken}`;
                    return instance(originalRequest);
                } catch (refreshError) {
                    isRefreshing = false;
                    refreshSubscribers = [];

                    // Refresh failed, so clear token and send user to login.
                    tokenStorage.clearAccessToken();
                    window.location.href = loginRedirectPath;
                    return Promise.reject(refreshError);
                }
            }

            return Promise.reject(error);
        }
    );

    return instance;
};

/**
 * Die gemeinsame Instanz, konfiguriert ausschliesslich aus `process.env.REACT_APP_*`.
 */
export const httpClient = createHttpClient();

export default httpClient;
